import React, { useEffect, useMemo, useState } from "react";
import { TrendingUp, Coins, Percent, CalendarDays, DollarSign, PiggyBank, ShieldCheck, Target } from "lucide-react";
import api from "@/lib/api";
import useCaPrice from "@/lib/useCaPrice";
import LiveStakingRates from "@/components/LiveStakingRates";
import Field from "@/components/calculator/Field";
import CompoundView from "@/components/calculator/CompoundView";
import RecoveryView from "@/components/calculator/RecoveryView";

// Fallback only — the live CaryPact plan table arrives from /staking-rates.
const FALLBACK_RATES = { flexible: 0.2, 30: 0.26, 90: 0.32, 180: 0.4, 360: 0.5, 540: 0.6 };
const PLANS = [
  { key: "flexible", label: "Flexible", days: null },
  { key: "30", label: "30d", days: 30 },
  { key: "90", label: "90d", days: 90 },
  { key: "180", label: "180d", days: 180 },
  { key: "360", label: "360d", days: 360 },
  { key: "540", label: "540d", days: 540 },
];
const STORAGE_KEY = "cp_calculator_v2";
const fmtUSD = (n) => (isFinite(n) ? `$${Math.round(n).toLocaleString()}` : "—");

const TABS = [
  { k: "compound", label: "Daily compounding + CA appreciation", short: "Compounding", icon: TrendingUp },
  { k: "recovery", label: "When do you get your money back", short: "Money back", icon: PiggyBank },
];

/**
 * One panel of inputs, two views. Both calculators always asked for the same
 * capital / rate / term, so they now share a single form and the tabs just swap
 * the chart: compounding growth, or capital recovery.
 */
export default function StakingCalculator() {
  const saved = (() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "null") || {}; } catch { return {}; }
  })();
  const [tab, setTab] = useState(saved.tab === "recovery" ? "recovery" : "compound");
  const [principal, setPrincipal] = useState(saved.principal ?? 10_000);
  const [dailyRate, setDailyRate] = useState(saved.dailyRate ?? FALLBACK_RATES["360"]);
  const [days, setDays] = useState(saved.days ?? 360);
  const [caPrice, setCaPrice] = useState(saved.caPrice ?? 30);
  const [monthlyAppn, setMonthlyAppn] = useState(saved.monthlyAppn ?? 0);
  const [milestone, setMilestone] = useState(saved.milestone ?? null);

  const { price: livePrice } = useCaPrice();
  useEffect(() => { if (livePrice) setCaPrice(+Number(livePrice).toFixed(2)); }, [livePrice]);

  // The real CaryPact plan table (synced from the dApp's own staking settings).
  const [rates, setRates] = useState(FALLBACK_RATES);
  useEffect(() => {
    api.get("/staking-rates")
      .then(({ data }) => { if (data?.rates) setRates(data.rates); })
      .catch(() => { /* keep fallback */ });
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ tab, principal, dailyRate, days, caPrice, monthlyAppn, milestone }));
    } catch { /* ignore quota / disabled storage */ }
  }, [tab, principal, dailyRate, days, caPrice, monthlyAppn, milestone]);

  const plans = useMemo(() => PLANS
    .map((p) => ({ ...p, rate: Number(rates?.[p.key]) }))
    .filter((p) => p.rate > 0 && !isNaN(p.rate)), [rates]);
  // Withdrawal target defaults to getting your capital back.
  const target = milestone == null || milestone === "" ? (Number(principal) || 0) : milestone;

  return (
    <section className="px-6 md:px-10 pt-8 pb-6" data-testid="staking-calculator">
      <div className="max-w-[1400px] mx-auto">
        <div className="pill mb-4"><span className="dot" /> CaryPact staking calculator · live plan rates</div>
        <h1 className="font-display text-3xl md:text-5xl font-semibold leading-[1.1] tracking-tight">
          Your numbers, <span className="gradient-text">two ways</span>.
        </h1>
        <p className="text-base text-ink-2 max-w-2xl mt-3 mb-6">
          Enter everything once on the left, then switch between the compounding curve and the
          capital-recovery view. Rates come straight from CaryPact&apos;s own plan table.
        </p>

        <div className="grid lg:grid-cols-[380px_1fr] gap-6">
          {/* ONE input panel for both views */}
          <div className="space-y-4">
            <div className="glass-strong p-6 space-y-4 h-fit" data-testid="calc-inputs">
              <div className="font-display text-sm font-bold uppercase tracking-wider text-ink-2">Your numbers</div>
              <Field label="Starting capital" icon={DollarSign} value={principal} onChange={setPrincipal} suffix="USDT" testid="calc-principal" />
              <Field label="Daily interest rate" icon={Percent} value={dailyRate} onChange={setDailyRate} suffix="%/day" hint="live CaryPact rates" testid="calc-daily-rate" />
              <Field label="Term" icon={CalendarDays} value={days} onChange={setDays} suffix="days" testid="calc-days" />
              <div>
                <div className="text-[10px] uppercase tracking-wider font-mono text-ink-3 mb-1.5">CaryPact plan · sets rate + term</div>
                <div className="flex gap-1.5 flex-wrap" data-testid="calc-plan-chips">
                  {plans.map((p) => {
                    const active = Number(dailyRate) === p.rate && (!p.days || Number(days) === p.days);
                    return (
                      <button key={p.key} type="button"
                        onClick={() => { setDailyRate(p.rate); if (p.days) setDays(p.days); }}
                        data-testid={`calc-plan-${p.key}`}
                        className="px-2.5 py-1 rounded-lg text-[11px] font-mono font-semibold transition-colors"
                        style={active
                          ? { background: "#4060FF", color: "white", border: "1px solid #4060FF" }
                          : { background: "rgba(255,255,255,0.55)", color: "#334155", border: "1px solid rgba(15,18,38,0.08)" }}>
                        {p.label} · {p.rate}%
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="h-px" style={{ background: "rgba(15,18,38,0.08)" }} />
              <div className="font-display text-sm font-bold uppercase tracking-wider text-ink-2 flex items-center gap-1.5">
                <Coins size={14} className="text-[#7C3AED]" /> CA price growth
              </div>
              <Field label={livePrice ? "Current CA price (live)" : "Current CA price"} icon={DollarSign} value={caPrice} onChange={setCaPrice} suffix="USD" testid="calc-ca-price" />
              <Field label="Monthly CA appreciation" icon={TrendingUp} value={monthlyAppn} onChange={setMonthlyAppn} suffix="%/mo" testid="calc-monthly-appn" />

              <div className="h-px" style={{ background: "rgba(15,18,38,0.08)" }} />
              <div className="font-display text-sm font-bold uppercase tracking-wider text-ink-2 flex items-center gap-1.5">
                <Target size={14} className="text-[#00863A]" /> Getting your money back
              </div>
              <Field label="Withdrawal target" icon={PiggyBank} value={target} onChange={setMilestone} suffix="USDT" hint="default = your capital" testid="calc-milestone" />
              <div className="rounded-xl p-3 flex items-start gap-2 text-[12px] text-ink-2 leading-relaxed"
                style={{ background: "rgba(0,134,58,0.07)", border: "1px solid rgba(0,134,58,0.22)" }} data-testid="calc-target-note">
                <ShieldCheck size={15} className="shrink-0 mt-0.5" style={{ color: "#00863A" }} />
                Pull {fmtUSD(target)} out the day profits reach it and your original capital is off the table —
                everything after that is house money.
              </div>
            </div>
            <LiveStakingRates />
          </div>

          {/* Two views, one set of numbers */}
          <div>
            <div className="glass p-1.5 mb-5 inline-flex gap-1 flex-wrap" data-testid="calc-tabs">
              {TABS.map((t) => {
                const active = tab === t.k;
                const Icon = t.icon;
                return (
                  <button key={t.k} type="button" onClick={() => setTab(t.k)}
                    data-testid={`calc-tab-${t.k}`}
                    className="px-3.5 py-2 rounded-xl text-[12px] font-semibold transition-colors inline-flex items-center gap-1.5"
                    style={active
                      ? { background: "#4060FF", color: "white" }
                      : { background: "transparent", color: "#475569" }}>
                    <Icon size={13} />
                    <span className="hidden sm:inline">{t.label}</span>
                    <span className="sm:hidden">{t.short}</span>
                  </button>
                );
              })}
            </div>

            {tab === "compound" ? (
              <CompoundView principal={principal} dailyRate={dailyRate} days={days} caPrice={caPrice} monthlyAppn={monthlyAppn} />
            ) : (
              <RecoveryView principal={principal} dailyRate={dailyRate} days={days} milestone={target} />
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
